import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { partyLetter } from '../../partyLetter';
import './LedgerParties.scss';

export type LedgerPartiesProps = {
  /** The parties in the order the document names them. */
  names: string[];
};

/**
 * The Party A and Party B columns: the first two parties, one a column, each
 * cut short with its full name on hover. A third party onwards folds into a
 * "+N" after Party B, which names them on hover. Empty where the document
 * names no party for the column: a lone dash floated in a wide column.
 */
export const LedgerParties: React.FC<LedgerPartiesProps> = ({ names }) => {
  const { t } = useTranslation();

  const [partyA, partyB, ...others] = names;

  const party = (
    column: 'party-a' | 'party-b',
    name: string | undefined,
    more?: React.ReactNode
  ) => (
    <span
      className={`tree-ledger-cell tree-ledger-cell--${column} tree-ledger-party`}
    >
      {name && (
        // Its own direction, so a Latin name on a right-to-left page is cut
        // short at its end rather than its start.
        <span className="tree-ledger-party__name" dir="auto" title={name}>
          {name}
        </span>
      )}
      {more}
    </span>
  );

  return (
    <>
      {party('party-a', partyA)}
      {party(
        'party-b',
        partyB,
        others.length > 0 && (
          <span
            className="tree-ledger-party__more"
            title={others
              .map(
                (name, index) =>
                  `${t('Party {{letter}}', {
                    letter: partyLetter(index + 2),
                  })}: ${name}`
              )
              .join('\n')}
          >
            +{others.length}
          </span>
        )
      )}
    </>
  );
};
